import React, { useEffect, useMemo } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Tab, Tabs } from '@mui/material';
import { Insights } from '@mui/icons-material';
import { useAuth } from '../auth/useAuth';
import { scopeForViewer, useClientTasks } from '../dashboard/tasks/useClientTasks';
import { EmptyState } from './components/EmptyState';
import { StaffDetailView } from './components/StaffDetailView';
import { TeamOverviewView } from './components/TeamOverviewView';
import { WorkloadView } from './components/WorkloadView';
import { useSelfStaffId, useStaffDoc, useStaffPerformance, useStaffPerformanceScope, useSyncMeta } from './hooks/useStaffPerformance';
import { useServiceCategoryFilter } from './hooks/useServiceCategoryFilter';
import { useSuccessUpdates } from './hooks/useSuccessUpdates';
import { StaffPerformanceScope } from './types';
import { applyCategoryFilter, applyCategoryFilterToAll, collectServiceCategories } from './utils/serviceCategories';
import { successUpdatesReady } from './utils/successUpdates';

const TEAM_PATH = '/staff-performance';
const NO_OWN_DATA = 'There is no performance data for your staff ID yet. Data refreshes every Monday and on the 1st of the month.';
const BACK_TO_TEAM_LABEL = 'Back to team';
const TAB_PARAM = 'tab';
const WORKLOAD_TAB = 'workload';

const Loader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
    <CircularProgress />
  </Box>
);

/**
 * Route container for /staff-performance and /staff-performance/:staffId.
 * Coaches always see their own page; managers and admins land on the team
 * overview and drill into any staff member their scope allows.
 */
export const StaffPerformancePage: React.FC = () => {
  const { staffId } = useParams<{ staffId: string }>();
  const navigate = useNavigate();
  // The service-category selection lives in the URL; every link out of the page keeps it.
  const { search } = useLocation();
  const { selected: categories, setSelected: setCategories } = useServiceCategoryFilter();
  const withSearch = (path: string) => `${path}${search}`;
  const backToTeam = { to: withSearch(TEAM_PATH), label: BACK_TO_TEAM_LABEL };
  const { userRole, userMetadata } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = useStaffPerformanceScope();
  const selfStaffId = useSelfStaffId();
  const { meta } = useSyncMeta();

  const isCoach = userRole === 'USER';
  // The Workload tab is for managers and admins on the team overview only; coaches never see it, whatever the URL says.
  const canSeeWorkload = (userRole === 'ADMIN' || userRole === 'MANAGER') && !staffId;
  const showWorkload = canSeeWorkload && searchParams.get(TAB_PARAM) === WORKLOAD_TAB;
  // Only read the client tasks while the tab is open; they are cached, so switching back and forth is cheap.
  const workloadTasks = useClientTasks(showWorkload ? scopeForViewer(userRole, userMetadata) : null);
  const setTab = (next: string) =>
    setSearchParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next === WORKLOAD_TAB) updated.set(TAB_PARAM, WORKLOAD_TAB);
        else updated.delete(TAB_PARAM);
        return updated;
      },
      { replace: true }
    );

  // Drilling in from the bottom of a long team table would otherwise open the
  // detail page scrolled to its end; the router does not reset scroll itself.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [staffId]);

  // The staff member whose detail page this is: a coach's own, or the one a manager / admin drilled into.
  const detailId = isCoach ? selfStaffId : staffId;

  // Hooks run unconditionally; a null id / scope means "no subscription".
  const detail = useStaffDoc(isCoach ? selfStaffId ?? undefined : staffId);
  const list = useStaffPerformance(!isCoach && !staffId ? scope : null);
  // A detail page reads just that person's document, so `'self'` here only means "one staff doc", not "the signed-in
  // user"; the team overview reads the whole scope.
  const updatesScope: StaffPerformanceScope | null = detailId ? { kind: 'self', staffId: detailId } : !isCoach ? scope : null;
  // Supplementary: its error is never rendered, so a denied or failed subscription leaves the page as it was.
  const successUpdates = useSuccessUpdates(updatesScope);
  // Passed to the detail view only once loaded with a successful run behind it, so until then it reads as it did before.
  const detailUpdates =
    detailId && successUpdatesReady(successUpdates.meta, successUpdates.docsLoaded)
      ? { updates: successUpdates.byStaffId.get(detailId) ?? null, meta: successUpdates.meta }
      : undefined;

  // Category filtering happens here, once, so every view below sees figures already restricted to the selection;
  // the options are collected from the unfiltered records so nothing disappears from the control.
  const teamRecords = useMemo(() => applyCategoryFilterToAll(list.records, categories), [list.records, categories]);
  const teamCategoryFilter = useMemo(
    () => ({ options: collectServiceCategories(list.records), selected: categories, onChange: setCategories }),
    [list.records, categories, setCategories]
  );
  const detailRecord = useMemo(() => (detail.record ? applyCategoryFilter(detail.record, categories) : null), [detail.record, categories]);
  const detailCategoryFilter = useMemo(
    () => ({ options: collectServiceCategories(detail.record ? [detail.record] : []), selected: categories, onChange: setCategories }),
    [detail.record, categories, setCategories]
  );

  const renderContent = (): React.ReactNode => {
    if (!scope) return <Loader />;

    if (isCoach) {
      if (staffId) return <Navigate to={TEAM_PATH} replace />;
      if (scope.kind === 'none') return <EmptyState title="No performance data yet" body={scope.reason} icon={<Insights />} />;
      if (detail.isLoading) return <Loader />;
      if (detail.error) return <Alert severity="error">{detail.error}</Alert>;
      if (!detailRecord) return <EmptyState title="No performance data yet" body={NO_OWN_DATA} icon={<Insights />} />;
      return <StaffDetailView record={detailRecord} meta={meta} successUpdates={detailUpdates} categoryFilter={detailCategoryFilter} />;
    }

    if (staffId) {
      if (detail.isLoading) return <Loader />;
      if (detail.error) {
        return (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" component={RouterLink} to={withSearch(TEAM_PATH)}>
                {BACK_TO_TEAM_LABEL}
              </Button>
            }
          >
            {detail.error}
          </Alert>
        );
      }
      if (!detailRecord) {
        return (
          <EmptyState
            title="Staff member not found"
            body="There is no performance record for this staff id in the latest sync."
            icon={<Insights />}
          />
        );
      }
      return (
        <StaffDetailView
          record={detailRecord}
          meta={meta}
          backTo={backToTeam}
          successUpdates={detailUpdates}
          categoryFilter={detailCategoryFilter}
        />
      );
    }

    if (scope.kind === 'none') return <EmptyState title="No team to show" body={scope.reason} icon={<Insights />} />;
    if (list.isLoading) return <Loader />;
    if (list.error) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={list.retry}>
              Retry
            </Button>
          }
        >
          {list.error}
        </Alert>
      );
    }
    return (
      <TeamOverviewView
        records={teamRecords}
        meta={meta}
        role={userRole === 'ADMIN' ? 'ADMIN' : 'MANAGER'}
        assignedLocations={scope.kind === 'location' ? scope.locations : []}
        selfStaffId={selfStaffId}
        successUpdates={successUpdates}
        categoryFilter={teamCategoryFilter}
        onOpenStaff={(record) => navigate(withSearch(`${TEAM_PATH}/${record.id}`))}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1450, mx: 'auto' }}>
      {canSeeWorkload ? (
        <Tabs
          value={showWorkload ? WORKLOAD_TAB : 'performance'}
          onChange={(_, next: string) => setTab(next)}
          aria-label="Team views"
          sx={{ mb: 3, minHeight: 40, borderBottom: '1px solid #E3E8EF', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: 15 } }}
        >
          <Tab value="performance" label="Performance" />
          <Tab value={WORKLOAD_TAB} label="Workload" />
        </Tabs>
      ) : null}
      {showWorkload ? <WorkloadView tasks={workloadTasks} isAdmin={userRole === 'ADMIN'} /> : renderContent()}
    </Box>
  );
};

export default StaffPerformancePage;
